import { useFormContext } from "react-hook-form";

import toast from "components/partials/toast/toast";

import { useAddCreativesWithWriterContext } from "components/modals/add-creatives-with-writer-modal/context/add-creatives-with-writer-context";
import { useCampaignWriterCreatives } from "components/modals/add-creatives-with-writer-modal/hooks/use-campaign-writer-creatives";
import type { AddCreativesWithWriterFormData } from "components/modals/add-creatives-with-writer-modal/add-creatives-with-writer-modal.types";

import { useRequiredApiContext } from "hooks/api/helpers/api-context";
import { useCreateWriterTouchpoints } from "hooks/api/use-touchpoints";

import { getErrorMessage } from "utilities";
import {
  clearEmailWriterPollingSession,
  markEmailWriterPollingStarted,
} from "utilities/email-writer-polling-session";

interface UseAddCreativesWithWriterResult {
  handleAddCreatives: () => void;
  isSubmitting: boolean;
}

export const useAddCreativesWithWriter = (): UseAddCreativesWithWriterResult => {
  const { setIsOpen, goTo, folderId } = useAddCreativesWithWriterContext();
  const { getValues } = useFormContext<AddCreativesWithWriterFormData>();
  const { clientId, campaignId } = useRequiredApiContext();
  const { mutate: createWriterTouchpoints, isPending } = useCreateWriterTouchpoints();
  const { creatives } = useCampaignWriterCreatives();

  const handleAddCreatives = () => {
    const { selectedCreativeIds, creativeTemplates, additionalInstructions } = getValues();
    const hasSelectedEmailCreative = creatives.some(
      (creative) => creative.touchpointChannel === "email" && selectedCreativeIds.includes(creative.id),
    );

    if (hasSelectedEmailCreative) {
      markEmailWriterPollingStarted(campaignId, clientId);
    }

    createWriterTouchpoints(
      {
        creatives: selectedCreativeIds.map((creativeId) => {
          const templateTouchpointVersionId = creativeTemplates[creativeId];

          return {
            creativeId,
            ...(templateTouchpointVersionId && { templateTouchpointVersionId }),
          };
        }),
        emailAdditionalInstructions: additionalInstructions || undefined,
        folderId,
      },
      {
        onSuccess: (createdTouchpoints) => {
          if (hasSelectedEmailCreative) {
            markEmailWriterPollingStarted(campaignId, clientId, {
              touchpointIds: createdTouchpoints.map((touchpoint) => touchpoint.id),
            });
            toast.info({
              data: {
                title: "Email writing in progress",
                message:
                  "Written emails content will appear in creative details, and notification will be sent upon completion.",
              },
              autoClose: false,
            });
          }
          toast.success({ data: { title: "Creatives added" } });
          setIsOpen(false);
          goTo(1);
        },
        onError: (error) => {
          clearEmailWriterPollingSession(campaignId);
          toast.error({
            data: {
              title: "Failed to add creatives",
              message: getErrorMessage(error) || "Please try again.",
            },
          });
        },
      },
    );
  };

  return { handleAddCreatives, isSubmitting: isPending };
};
